import './SideNavContent.scss';
import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { LEGACY_APP_URL, PROJECT_ID_PLACEHOLDER, PROJECT_ID_PREFIX } from 'common/constants';
import { useCompareUrlWithProjectId, useNavigateWithProjectId } from 'common/hooks/route';
import useSignOut from 'common/hooks/useSignOut';
import { AppModule, MenuItem } from 'common/models/page-config.models';
import { selectLastActiveProjects, selectModules } from 'common/store/slices/page-config.slice';
import { useSelector } from 'react-redux';
import SideNavModuleItem from './SideNavModuleItem';
import SideNavUserMenu, { supportLink, signOutLink } from './SideNavUserMenu';
import { useResize } from 'common/hooks/resize';
import { ProjectSelectorContent } from '../ProjectSelectorContent';
import SideNavModuleChildItem from './SideNavModuleChildItem';
import { MODULE_ROUTES, PROFILE_CAPTURE_ROUTES } from 'routes/routes.const';
import { useParams } from 'react-router-dom';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { buildTree } from 'common/hooks/utils';

interface SideNaveContentProps {
  isSidebarOpen: boolean;
  expanded: string;
  setExpanded: (id: string) => void;
  trayContent?: MenuItem[];
  setTrayContent?: (content: MenuItem[]) => void;
  closeSidebar?: () => void;
}

function SideNavContent({
  isSidebarOpen,
  closeSidebar,
  expanded,
  setExpanded,
  trayContent,
  setTrayContent,
}: Readonly<SideNaveContentProps>) {
  const { projectId: urlProjectId } = useParams();
  const modules = useSelector(selectModules);
  const { activeModule, activeProject, captureProjects } = useModuleProjectContext();
  const navigate = useNavigateWithProjectId();
  const { isMobile } = useResize();
  const { signout } = useSignOut();
  const { compareUrl } = useCompareUrlWithProjectId();
  const isProfileCapture = compareUrl(PROFILE_CAPTURE_ROUTES.root, { includes: true });
  const lastActiveProjects = useSelector(selectLastActiveProjects);

  const reducedNavItems = [supportLink, signOutLink];

  const getChildren = (module: AppModule): MenuItem[] => {
    if (module.routeIsExternalLink || isMobile) {
      return [];
    }

    if (module.moduleID === activeModule?.moduleID) {
      return buildTree(activeProject?.pages ?? [], activeProject?.moduleProjectID);
    }

    const lastProjectId = lastActiveProjects[module.moduleID];
    if (lastProjectId) {
      const lastProject = modules
        ?.find((moduleItem) => moduleItem.moduleID === module.moduleID)
        ?.projects.find((projectItem) => projectItem.moduleProjectID === lastProjectId);

      if (lastProject) {
        return buildTree(lastProject.pages ?? [], lastProject.moduleProjectID);
      }
    }

    const project = modules.find((moduleItem) => moduleItem.moduleID === module.moduleID)
      ?.projects[0];

    if (project) {
      return buildTree(project?.pages ?? [], project?.moduleProjectID ?? '');
    }

    return [];
  };

  const insightsModuleRoute = MODULE_ROUTES.insights.replace(`/${PROJECT_ID_PLACEHOLDER}`, '');

  const moduleItems: MenuItem[] = modules
    ?.filter((module: AppModule) =>
      isMobile ? module.route === insightsModuleRoute || module.routeIsExternalLink : true
    )
    ?.map((module: AppModule) => {
      if (isMobile && module.route === insightsModuleRoute && captureProjects.length > 0) {
        return {
          ...module,
          moduleName: 'Insights Capture',
          moduleIconName: 'CAPTURE_MENU_ICON',
          children: [],
          customChildren: <ProjectSelectorContent onChange={closeSidebar} />,
        };
      }

      return module;
    })
    ?.map((module) => ({
      sortOrder: module.moduleSortOrder,
      id: module.moduleID.toString(),
      icon: KWELLO_ICON[module.moduleIconName as keyof typeof KWELLO_ICON],
      text: module.moduleName,
      tag: module.moduleTag,
      route: module.route,
      children: getChildren(module), // module.moduleName === 'Insights' ? projectMenuItems : [],
      customChildren: module?.customChildren,
      external: module.routeIsExternalLink,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const getPath = (route: string): string => {
    if (route.endsWith('/social-monitoring')) {
      return LEGACY_APP_URL + '/social-monitoring';
    }
    if (route.endsWith('/insights')) {
      return LEGACY_APP_URL + '/insights';
    }
    if (route.endsWith('/kol-planning')) {
      return LEGACY_APP_URL + '/kol-planning';
    }
    return route;
  };

  const toggleExpanded = (id: string) => {
    if (expanded === id) {
      setExpanded('');
    } else {
      setExpanded(id);
    }
  };

  const onSelectModule = (item: MenuItem) => {
    if (item.external && item.route) {
      const path = getPath(item.route);
      closeSidebar?.();
      window.open(path, '_blank', 'noreferrer');
      return;
    }

    if (item.route && !item.children?.length) {
      closeSidebar?.();
      window.open(item.route, '_blank', 'noreferrer');
    }

    toggleExpanded(item.id);
  };

  const onSelectModuleChild = (item: MenuItem) => {
    const filteredChildren = item.children?.filter((child) => !child.isTabBar);

    if (item.id === 'sign_out') {
      closeSidebar?.();
      signout();
    }

    if (item.route && !filteredChildren?.length) {
      if (trayContent?.length ?? 0 > 0) {
        setTrayContent?.([]);
      }
      closeSidebar?.();

      navigate(
        item.route.replace(
          PROJECT_ID_PLACEHOLDER,
          `${PROJECT_ID_PREFIX}${item.projectId ?? (urlProjectId as string)}`
        )
      );

      return;
    }

    if (item.children?.length) {
      setTrayContent?.(item.children);
    } else {
      closeSidebar?.();
      setTrayContent?.([]);
    }
  };

  return (
    <>
      <div className="side-nav-items">
        {!isProfileCapture &&
          moduleItems?.map((item) => {
            return (
              <SideNavModuleItem
                key={item.id}
                {...item}
                open={isSidebarOpen}
                onClick={onSelectModule}
                onChildClick={onSelectModuleChild}
                expanded={expanded}
              />
            );
          })}
        {isProfileCapture &&
          reducedNavItems.map((item) => (
            <SideNavModuleChildItem
              {...item}
              key={item.id}
              onClick={() => onSelectModuleChild(item)}
            />
          ))}
      </div>
      <SideNavUserMenu
        isSidebarOpen={isSidebarOpen}
        onClick={onSelectModule}
        onChildClick={onSelectModuleChild}
        expanded={expanded}
      />
    </>
  );
}

export default SideNavContent;
